// Helpers
import { getFormatUTCDate } from "@/Common/helpers/Dates";
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
// Custom components
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { InformationTable } from "../../InformationTable";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
// Materials
import { GridColDef } from "@mui/x-data-grid-premium";
// Types and constants
import { CommonSalesTableProps } from "@/Washouts/types/commons";
import { HEADER_CLASS_NAME, ID_GRID_COLDEF } from "@/Washouts/constants";
import { Issue } from "@/Common/types/tables";
import { DATE_FORMAT } from "@/Constants/date";

function getInitialColumns(reference: string, issues: Issue[]): GridColDef[] {
	return [
		ID_GRID_COLDEF,
		{
			field: "date",
			headerName: "Date",
			width: 130,
			resizable: false,
			type: "date",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { noWrap: true }, {}, reference),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateDashShortYear),
			renderCell: (params) => (
				<TypographyCell reference={reference} params={params} issues={issues} hints={{ noWrap: isFooterCell(params) }}>
					{isFooterCell(params) ? "Opportunity Mining Total" : null}
				</TypographyCell>
			),
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "customer",
			headerName: "Customer",
			width: 184,
			minWidth: 184,
			maxWidth: 284,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}, reference),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "stockNumber",
			headerName: "Stock #",
			width: 110,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}, reference),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "saleType",
			headerName: "Type",
			width: 110,
			resizable: false,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}, reference),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "amount",
			headerName: "Amount",
			width: 110,
			resizable: false,
			type: "number",
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { isMoney: true }, {}, reference),
			renderCell: (params) => (
				<TypographyCell render="absolute" reference={reference} params={params} issues={issues} hints={{ isMoney: true }} />
			),
			renderHeader: (params) => <TypographyHeader params={params} right />,
		},
	];
}

const negatives = ["amount"];

export default function AutoAlertsTable(props: Readonly<CommonSalesTableProps>) {
	if (props.loading) {
		return <TableLoader smallTitle content sx={{ padding: "15px 10px 0px 10px" }} />;
	}

	return (
		<>
			{props.dataPresent && (
				<InformationTable
					{...props}
					title={props.title ?? "Opportunity Mining"}
					colDefFn={getInitialColumns}
					negativeColumns={negatives}
				/>
			)}
		</>
	);
}
